/**
 * Shopper routes for customer-facing pages
 * All routes use ShopperLayout with navigation, drawer, and cart
 */

import type { RouteRecordRaw } from 'vue-router'

export const shopperRoutes: RouteRecordRaw[] = [
  {
    path: '/',
    component: () => import('@/layouts/ShopperLayout.vue'),
    children: [
      {
        path: '',
        name: 'Home',
        component: () => import('@/views/HomePage.vue'),
        meta: { title: 'Home' }
      },
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/auth/AuthPage.vue'),
        meta: { title: 'Sign In' }
      },
      {
        path: 'about',
        name: 'About',
        component: () => import('@/views/info/AboutPage.vue'),
        meta: { title: 'About Us' }
      },
      {
        path: 'products/:storeSlug?',
        name: 'Products',
        component: () => import('@/views/catalog/ProductsPage.vue'),
        props: true,
        meta: { title: 'Products' }
      },
      {
        path: 'stores',
        name: 'Stores',
        component: () => import('@/views/catalog/StoresPage.vue'),
        meta: { title: 'Stores' }
      },
      {
        path: 'store/create',
        name: 'StoreCreation',
        component: () => import('@/views/catalog/StoreCreationPage.vue'),
        meta: { title: 'Create Store' }
      },
      {
        path: 'store/:id',
        name: 'StoreDetail',
        component: () => import('@/views/catalog/StoreDetailPage.vue'),
        props: true,
        meta: { title: 'Store Details' }
      },
      {
        path: 'store/:slug/products',
        name: 'ProductDetail',
        component: () => import('@/views/catalog/ProductDetailPage.vue'),
        props: true,
        meta: { title: 'Product Details' }
      },
      {
        // Legacy path with plu as param -> redirect to query param
        path: 'store/:slug/products/:plu',
        redirect: (to) => ({
          name: 'ProductDetail',
          params: { slug: to.params.slug },
          query: { ...to.query, plu: to.params.plu },
        }),
      },
      {
        path: 'checkout/:storeSlug',
        name: 'Checkout',
        component: () => import('@/views/checkout/CheckoutPage.vue'),
        props: true,
        meta: { title: 'Checkout' }
        // Note: Auth check handled by page component itself to show custom login UI
      },
      {
        path: 'orders',
        name: 'Orders',
        component: () => import('@/views/orders/OrdersPage.vue'),
        meta: { 
          requiresAuth: true,
          title: 'Order History'
        }
      },
      {
        path: 'orders/:id',
        name: 'OrderDetail',
        component: () => import('@/views/orders/OrderDetailPage.vue'),
        meta: { 
          requiresAuth: true,
          title: 'Order Details'
        },
        props: true,
      },
      {
        path: 'profile',
        name: 'Profile',
        component: () => import('@/views/account/ProfilePage.vue'),
        meta: { 
          requiresAuth: true,
          title: 'Profile'
        }
      },
      {
        path: 'verify-email',
        name: 'VerifyEmail',
        component: () => import('@/views/auth/EmailVerificationPage.vue'),
        meta: { title: 'Verify Email' }
      },
      {
        path: 'cart',
        name: 'Cart',
        component: () => import('@/views/checkout/CartPage.vue'),
        meta: { title: 'Shopping Cart' }
      },
      {
        path: 'recently-viewed',
        name: 'RecentlyViewed',
        component: () => import('@/views/account/RecentlyViewedPage.vue'),
        meta: { title: 'Recently Viewed' }
      },
      {
        path: 'wishlist',
        name: 'Wishlist',
        component: () => import('@/views/account/WishlistPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Wishlist'
        }
      },
      {
        path: 'faq',
        name: 'FAQ',
        component: () => import('@/views/info/FAQPage.vue'),
        meta: { title: 'FAQ' }
      },
      {
        path: 'contact',
        name: 'Contact',
        component: () => import('@/views/info/ContactPage.vue'),
        meta: { title: 'Contact Us' }
      },
      {
        path: 'categories',
        name: 'Categories',
        component: () => import('@/views/catalog/CategoriesPage.vue'),
        meta: { title: 'Categories' }
      },
      {
        path: 'prescriptions',
        name: 'Prescriptions',
        component: () => import('@/views/prescriptions/PrescriptionsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Prescriptions'
        }
      },
      {
        path: 'prescriptions/:id',
        name: 'PrescriptionDetail',
        component: () => import('@/views/prescriptions/PrescriptionDetailPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Prescription Detail'
        },
        props: true,
      },
      {
        path: 'store/:id/prescriptions/new',
        name: 'PrescriptionSubmit',
        component: () => import('@/views/prescriptions/PrescriptionSubmitPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Submit Prescription'
        }
      },
    ],
  },
]

